/**
 * PROMPT pipeline aggregation — teams-tab SkillsTab/skills-helpers.ts의 핵심 로직 포팅.
 */

import type {
  ConverterHistoryEntry,
  SkillHistoryEntry,
  FixLoopPromptStatus,
} from "../api/types";

export type ConverterStatus = "success" | "failed" | "not_run" | "no_file";

export interface PromptRow {
  promptId: string;
  converterStatus: ConverterStatus;
  converterEntry: ConverterHistoryEntry | null;
  recordCount: number | null;
  error: string | null;
  failFix: SkillHistoryEntry | null;
  validateFix: SkillHistoryEntry | null;
  fixLoop: FixLoopPromptStatus | null;
}

export type NextAction =
  | "run_converter"
  | "run_fail_fix"
  | "fail_fix_running"
  | "run_validate_fix"
  | "validate_fix_running"
  | "done";

export function yesterdayBsda(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
}

export function formatTime(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ko-KR", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const NO_FILE_PATTERN = /파일 없음|파일을 찾을 수 없습니다|파일이 없습니다/;

export function isNoFileEntry(entry: ConverterHistoryEntry): boolean {
  if (entry.status === "no_file") return true;
  if (entry.error && NO_FILE_PATTERN.test(entry.error)) return true;
  const results = entry.converter_response?.results ?? entry.converter_response?.conversions;
  if (results) {
    return results.some(
      (r: { error?: string }) =>
        typeof r.error === "string" && NO_FILE_PATTERN.test(r.error),
    );
  }
  return false;
}

export function getNextAction(row: PromptRow): NextAction {
  if (row.converterStatus === "not_run" || row.converterStatus === "no_file") {
    return "run_converter";
  }
  if (row.converterStatus === "failed") {
    if (!row.failFix || row.failFix.status === "failed") return "run_fail_fix";
    if (row.failFix.status === "running") return "fail_fix_running";
    if (row.failFix.status === "completed") {
      if (!row.validateFix || row.validateFix.status === "failed") return "run_validate_fix";
      if (row.validateFix.status === "running") return "validate_fix_running";
      return "done";
    }
    return "run_fail_fix";
  }
  // success
  if (!row.validateFix || row.validateFix.status === "failed") return "run_validate_fix";
  if (row.validateFix.status === "running") return "validate_fix_running";
  return "done";
}

/**
 * 전체 prompt 리스트 + converter history + skill history + fix_loop 를 병합해서
 * PromptRow[] 형태로 반환.
 */
export function buildPromptPipeline(params: {
  prompts: string[];
  converterHistory: ConverterHistoryEntry[];
  skillHistory: SkillHistoryEntry[];
  fixLoop: FixLoopPromptStatus[];
}): PromptRow[] {
  const { prompts, converterHistory, skillHistory, fixLoop } = params;

  // prompt_id 별 가장 최신 converter entry
  const latestConverter = new Map<string, ConverterHistoryEntry>();
  for (const e of converterHistory) {
    const prev = latestConverter.get(e.prompt_id);
    if (!prev || new Date(e.started_at) > new Date(prev.started_at)) {
      latestConverter.set(e.prompt_id, e);
    }
  }

  // skill history — prompt 추출이 까다로움. resultSummary 에 prompt_id 가 있거나, 별도 필드.
  // 현재 API 는 skill 실행 단위로 기록되므로 prompt 연관은 느슨함 — skillName + 가장 최근 매칭.
  const latestSkill = (name: string): SkillHistoryEntry | null => {
    const matches = skillHistory
      .filter((s) => s.skill === name)
      .sort((a, b) =>
        (b.startedAt ?? "").localeCompare(a.startedAt ?? ""),
      );
    return matches[0] ?? null;
  };

  const failFix = latestSkill("fail_fix");
  const validateFix = latestSkill("validate_fix");

  const fixLoopMap = new Map<string, FixLoopPromptStatus>(
    fixLoop.map((p) => [p.promptId, p]),
  );

  return prompts.map((pid) => {
    const entry = latestConverter.get(pid) ?? null;
    let converterStatus: ConverterStatus;
    if (!entry) converterStatus = "not_run";
    else if (isNoFileEntry(entry)) converterStatus = "no_file";
    else converterStatus = entry.status === "success" ? "success" : "failed";

    return {
      promptId: pid,
      converterStatus,
      converterEntry: entry,
      recordCount: entry?.record_count ?? null,
      error: entry?.error ?? null,
      failFix: converterStatus === "failed" ? failFix : null,
      validateFix:
        converterStatus === "success" ||
        (converterStatus === "failed" && failFix?.status === "completed")
          ? validateFix
          : null,
      fixLoop: fixLoopMap.get(pid) ?? null,
    };
  });
}

export function fixLoopLabel(p: FixLoopPromptStatus): string {
  const s = (p.status || "").toUpperCase();
  if (s === "PASS") {
    return p.attempts === 0 ? "✓ 자동통과" : `✓ 수정후통과 (${p.attempts}회)`;
  }
  if (s === "IN_PROGRESS") return `진행중 ${p.attempts}/${p.maxRetries}`;
  if (s === "ESCALATED") return "에스컬레이션";
  return s;
}

/** Tooltip 용 상세 설명. UI에서 hover 시 표시. */
export function fixLoopTooltip(p: FixLoopPromptStatus): string {
  const s = (p.status || "").toUpperCase();
  if (s === "PASS") {
    if (p.attempts === 0) {
      return "컨버터가 첫 실행에 검증까지 통과 — 에이전트 개입 없이 성공.";
    }
    return `에이전트가 script.py를 ${p.attempts}회 수정한 뒤 최종 검증 통과. 자동 복구됨.`;
  }
  if (s === "IN_PROGRESS") {
    return `에이전트가 수정 시도 중. ${p.attempts}/${p.maxRetries}회 시도${p.pattern ? ` · 감지 패턴: ${p.pattern}` : ""}.`;
  }
  if (s === "ESCALATED") {
    return `${p.maxRetries}회 시도했지만 해결 실패 — 사람 검토 필요.${p.escalationReason ? ` (${p.escalationReason})` : ""}`;
  }
  return p.status;
}

/** BSDA 기준 파이프라인 요약 — 상단 배너 용. */
export interface PromptPipelineSummary {
  total: number;
  done: number;          // 컨버터 성공 + fix_loop PASS
  inProgress: number;    // fix_loop IN_PROGRESS
  failed: number;        // 컨버터 실패 + fix_loop 미해결
  noFile: number;        // converter_no_file (파일 부재, 정상 skip 범주)
  notRun: number;        // 아직 시도 안됨
}

export function summarizePipeline(rows: PromptRow[]): PromptPipelineSummary {
  const s: PromptPipelineSummary = { total: rows.length, done: 0, inProgress: 0, failed: 0, noFile: 0, notRun: 0 };
  for (const r of rows) {
    const loopStatus = r.fixLoop?.status?.toUpperCase();
    if (r.converterStatus === "not_run") s.notRun++;
    else if (r.converterStatus === "no_file") s.noFile++;
    else if (loopStatus === "IN_PROGRESS") s.inProgress++;
    else if (loopStatus === "PASS") s.done++;
    else if (loopStatus === "ESCALATED" || loopStatus === "FAIL" || loopStatus === "ERROR") s.failed++;
    else if (r.converterStatus === "success") s.done++;
    else s.failed++;
  }
  return s;
}

export function fixLoopColor(
  status: string,
): "success" | "warning" | "error" | "default" {
  switch ((status || "").toUpperCase()) {
    case "PASS":
      return "success";
    case "IN_PROGRESS":
      return "warning";
    case "ESCALATED":
    case "FAIL":
    case "ERROR":
      return "error";
    default:
      return "default";
  }
}
